import type { RefObject } from "react";
import { useEffect } from "react";

import { scrollActiveOptionIntoView } from "./dropdown-options-scroll";

interface UseDropdownActiveOptionScrollInput {
  isOpen: boolean;
  effectiveActiveIndex: number | null;
  optionsLength: number;
  optionsContainerRef: RefObject<HTMLDivElement | null>;
  optionItemRefs: RefObject<Array<HTMLDivElement | null>>;
}

interface ActiveOptionScrollElements {
  optionsContainer: HTMLDivElement;
  activeItem: HTMLDivElement;
}

function getActiveItem(input: {
  effectiveActiveIndex: number | null;
  optionItemRefs: RefObject<Array<HTMLDivElement | null>>;
}): HTMLDivElement | null {
  if (input.effectiveActiveIndex === null) {
    return null;
  }

  return input.optionItemRefs.current[input.effectiveActiveIndex] ?? null;
}

function getActiveOptionScrollElements(input: {
  isOpen: boolean;
  effectiveActiveIndex: number | null;
  optionsContainerRef: RefObject<HTMLDivElement | null>;
  optionItemRefs: RefObject<Array<HTMLDivElement | null>>;
}): ActiveOptionScrollElements | null {
  if (!input.isOpen) {
    return null;
  }

  const optionsContainer = input.optionsContainerRef.current;
  if (!optionsContainer) {
    return null;
  }

  const activeItem = getActiveItem(input);
  if (!activeItem) {
    return null;
  }

  return { optionsContainer, activeItem };
}

export function useDropdownActiveOptionScroll({
  isOpen,
  effectiveActiveIndex,
  optionsLength,
  optionsContainerRef,
  optionItemRefs,
}: UseDropdownActiveOptionScrollInput): void {
  useEffect(() => {
    const elements = getActiveOptionScrollElements({
      isOpen,
      effectiveActiveIndex,
      optionsContainerRef,
      optionItemRefs,
    });
    if (!elements || effectiveActiveIndex === null) {
      return;
    }

    scrollActiveOptionIntoView({
      optionsContainer: elements.optionsContainer,
      activeItem: elements.activeItem,
      effectiveActiveIndex,
      optionsLength,
    });
  }, [
    effectiveActiveIndex,
    isOpen,
    optionItemRefs,
    optionsContainerRef,
    optionsLength,
  ]);
}
